import { Base } from '@studiometa/js-toolkit';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
import { SplitText } from 'gsap/SplitText.js';
gsap.registerPlugin(ScrollTrigger, SplitText);

/**
 * ScreenBento component
 */
export default class ScreenBento extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'ScreenBento',
    refs: ['content[]'],
    options: {
      name: {
        type: String,
        default: '',
      },
    },
  };

  /**
   * mounted
   */
  mounted() {
    this.initializeSplitText();
    this.animateContent();
  }

  /**
   * Initialize SplitText for large text elements
   */
  initializeSplitText() {
    this.splitTextElements = [];
    this.smallTextElements = [];

    this.$refs.content.forEach((contentElement) => {
      const largeText = contentElement.querySelector('.bento-title');
      const smallTexts = contentElement.querySelectorAll('.bento-subtitle');

      if (largeText) {
        const splitText = new SplitText(largeText, { type: 'words, chars' });
        this.splitTextElements.push({
          element: largeText,
          splitText,
          chars: splitText.chars,
        });
        gsap.set(splitText.chars, { opacity: 0 });
      }

      if (smallTexts.length > 0) {
        this.smallTextElements.push(...smallTexts);
        gsap.set(smallTexts, { opacity: 0 });
      }
    });
  }

  /**
   * Animate content with timeline scheduling
   */
  animateContent() {
    const mainTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: `#${this.$options.name}`,
        toggleActions: 'play reverse play reverse',
        end: 'bottom 300px',
        onReverseComplete: () => {
          this.resetTextElements();
        },
      },
    });

    mainTimeline.to(this.$el, {
      zIndex: 2,
    });

    mainTimeline.to(this.$refs.content, {
      duration: 0.2,
      scale: 1,
      stagger: {
        amount: 0.2,
        from: 'random',
      },
      ease: 'out.out',
    });

    const randomElementOrder = [...Array(this.splitTextElements.length).keys()].sort(
      () => Math.random() - 0.5,
    );

    randomElementOrder.forEach((originalIndex, staggerIndex) => {
      const textObj = this.splitTextElements[originalIndex];
      if (textObj.chars) {
        const elementStartTime = 0.4 + staggerIndex * 0.1;

        mainTimeline.set(textObj.element, { opacity: 1 }, elementStartTime);

        const randomIndices = [...Array(textObj.chars.length).keys()].sort(
          () => Math.random() - 0.5,
        );
        randomIndices.forEach((charIndex, i) => {
          const char = textObj.chars[charIndex];
          const charDelay = (i / textObj.chars.length) * 0.15;

          mainTimeline.fromTo(
            char,
            { opacity: 0 },
            {
              opacity: 1,
              duration: 0.08,
              ease: 'expo.out',
            },
            elementStartTime + charDelay,
          );
        });
      }
    });

    if (this.smallTextElements.length > 0) {
      mainTimeline.to(
        this.smallTextElements,
        {
          opacity: 1,
          duration: 0.4,
          stagger: {
            amount: 0.2,
            from: 'random',
          },
          ease: 'expo.out',
        },
        0.7,
      );
    }
  }

  /**
   * Reset text elements to initial state
   */
  resetTextElements() {
    this.splitTextElements.forEach((textObj) => {
      gsap.set(textObj.element, { opacity: 0 });
      gsap.set(textObj.chars, { opacity: 0 });
    });

    if (this.smallTextElements.length > 0) {
      gsap.set(this.smallTextElements, { opacity: 0 });
    }
  }
}
